import React, { useState, useMemo, useEffect } from 'react';
import { Country } from 'country-state-city';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { productService } from '../../_services/product.service';
import { resolveZone, eligibleRates, normaliseCountry } from './shippingRates';
import { cartTaxBreakdown } from '../taxes/sellerTax';
import { DISCOUNTS_VISIBLE } from '../../config/featureFlags';
import { useBookingCode } from '../discounts/client/useBookingCode';
import { CodeBox } from '../discounts/client/BookingCode';

// PhoneInput holds E.164. Older account records store national UK numbers
// ("07700…"), so lift those to +44 on prefill; anything already + is kept.
const toE164 = (raw) => {
  const v = String(raw || '').replace(/[\s\-()]/g, '');
  if (!v) return '';
  if (v.startsWith('+')) return v;
  if (v.startsWith('00')) return `+${v.slice(2)}`;
  if (v.startsWith('0')) return `+44${v.slice(1)}`;
  return `+44${v}`;
};

// UK habit: typing the leading 0 after the +44 ("+44 07700…") would make an
// invalid number. A valid +44 number never has 0 next, so drop it as they type.
const dropTrunkZero = (v) => (v && v.startsWith('+440') ? `+44${v.slice(4)}` : v);

// Human-readable name for an ISO country code (browser-native, no dataset).
const regionName = (() => {
  let dn = null;
  try { dn = new Intl.DisplayNames(['en'], { type: 'region' }); } catch (e) { /* old browser */ }
  return (code) => { try { return (dn && dn.of(code)) || code; } catch (e) { return code; } };
})();

// Checkout: collect vs deliver, with the salon's real shipping engine — the
// destination zone's eligible rates (flat / free-over / by-value / by-weight)
// are shown for the cart, the customer picks one, and on Pay we create the order
// server-side and redirect to Stripe (full amount to the salon less Beu fees).
// The server re-resolves + re-prices the rate; this UI is display only.

const readAccount = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (e) { return null; }
};
const isEmail = (s) => /\S+@\S+\.\S+/.test(s || '');

const TruckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 6h11v9H3zM14 9h4l3 3v3h-7z" /><circle cx="7" cy="18" r="1.6" /><circle cx="17" cy="18" r="1.6" />
  </svg>
);
const StoreIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 9.5 5.2 4h13.6L20 9.5M4 9.5h16M4 9.5v10.5h16V9.5M9 20v-5h6v5" /><path d="M4 9.5a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0" />
  </svg>
);

const ProductCheckout = ({ lines, subtotal, currency = '£', salonName, salonId, onBack, onRequireAuth }) => {
  const account = useMemo(readAccount, []);
  const [store, setStore] = useState(null);
  const [storeLoading, setStoreLoading] = useState(true);
  const [method, setMethod] = useState('collect'); // 'ship' | 'collect'
  const [country, setCountry] = useState('GB');
  const [rateId, setRateId] = useState('');
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: String(account ? (account.name || `${account.firstName || ''} ${account.lastName || ''}`.trim()) : ''),
    email: String(account?.email || ''),
    phone: toE164(account?.phone || account?.mobile || account?.phoneNumber),
    line1: '', city: '', postcode: '', note: '',
  });
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));
  const [phoneKey, setPhoneKey] = useState(0); // remount on blur re-derives the pretty +44 display

  useEffect(() => {
    let alive = true;
    productService.getPublicStore(salonId)
      .then((s) => {
        if (!alive) return;
        setStore(s || {});
        setStoreLoading(false);
        const zones = (s && s.shipping && s.shipping.zones) || [];
        if (s && s.shipping && s.shipping.enabled && zones.length) setMethod('ship');
        else if (s && s.pickup && s.pickup.enabled) setMethod('collect');
      })
      .catch(() => { if (alive) { setStore({}); setStoreLoading(false); } });
    return () => { alive = false; };
  }, [salonId]);

  const zones = (store && store.shipping && store.shipping.zones) || [];
  const shippingEnabled = !!(store && store.shipping && store.shipping.enabled && zones.length);
  const pickupEnabled = !!(store && store.pickup && store.pickup.enabled);

  const totalWeight = useMemo(() => (lines || []).reduce((s, l) => s + (Number(l.weight) || 0) * (l.qty || 1), 0), [lines]);

  // Destinations the salon actually ships to — the customer picks from these
  // instead of typing a country (the old free-text ISO field meant "UNITED
  // KINGDOM" failed to match a "GB" zone). A '*' zone opens the full list.
  const shipCountries = useMemo(() => {
    const codes = new Set();
    let worldwide = false;
    zones.forEach((z) => (z.countries || []).forEach((c) => {
      const u = normaliseCountry(c);
      if (u === '*') worldwide = true; else if (u) codes.add(u);
    }));
    let list;
    if (worldwide) {
      list = Country.getAllCountries().map((c) => ({ code: c.isoCode, name: c.name }));
    } else {
      list = [...codes].map((code) => ({ code, name: regionName(code) }));
    }
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [zones]);

  // Keep the selected country valid for this store (prefer GB).
  useEffect(() => {
    if (!shipCountries.length) return;
    if (!shipCountries.find((c) => c.code === country)) {
      setCountry(shipCountries.find((c) => c.code === 'GB') ? 'GB' : shipCountries[0].code);
    }
  }, [shipCountries]); // eslint-disable-line react-hooks/exhaustive-deps

  const zone = useMemo(() => resolveZone(zones, country), [zones, country]);
  // Discount code (beu-node-v2 discountService.applyToStoreOrder): checked
  // against the catalogue lines, taken off before delivery and tax — the
  // server prices it again on Pay, this is what the client sees first.
  const code = useBookingCode({ enabled: DISCOUNTS_VISIBLE, salonId, scopeMessage: 'That code doesn’t cover these products.' });
  const codeCtx = useMemo(() => ({
    lines: lines.map((l, i) => ({ id: `p${i}`, kind: 'product', serviceId: String(l.id), categoryId: null, stylistId: null, price: Number(l.price) || 0, qty: Number(l.qty) || 1 })),
    client: { userId: localStorage.getItem('userId') || null, phone: (account && (account.phoneNumber || account.phone)) || null },
  }), [lines, account]);
  const codeOn = !!(code.typed && code.result && code.result.ok);
  const lineOff = (i) => (codeOn ? Number(((code.result.lines || [])[i] || {}).amount) || 0 : 0);
  const discount = codeOn ? Number(code.result.amount) || 0 : 0;
  const discountedSubtotal = Math.max(0, Math.round((subtotal - discount) * 100) / 100);
  const rates = useMemo(() => eligibleRates(zone, { subtotal: discountedSubtotal, weight: totalWeight }), [zone, discountedSubtotal, totalWeight]);

  // Keep a valid rate selected as cart/destination changes.
  useEffect(() => {
    if (method !== 'ship') return;
    if (!rates.find((r) => String(r._id) === String(rateId))) setRateId(rates.length ? String(rates[0]._id) : '');
  }, [rates, method]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedRate = rates.find((r) => String(r._id) === String(rateId)) || null;
  const shipping = method === 'ship' ? (selectedRate ? Number(selectedRate.price) || 0 : 0) : 0;
  // Tax (docs/tax-money-path.md §4): the owner's rates come with the public
  // store. Exclusive mode adds the tax on top here AND as a Stripe line, so the
  // number on the button is the number on the card. Inclusive shows "Includes".
  const taxLines = useMemo(() => {
    const taxStore = store && store.tax;
    if (!taxStore) return null;
    // Tax on what's paid: each line after its share of the code.
    const items = lines.map((l, i) => ({ type: 'product', productId: l.id, price: l.price, quantity: l.qty, value: (Number(l.price) || 0) * (Number(l.qty) || 1) - lineOff(i) }));
    const shipTaxId = store && store.shipping && store.shipping.taxRateId;
    if (shipping > 0 && shipTaxId) items.push({ type: 'shipping', id: 'shipping', price: shipping, quantity: 1, taxIds: [shipTaxId] });
    return cartTaxBreakdown(taxStore, items, 1, (it) => (it.value != null ? it.value : (Number(it.price) || 0) * (Number(it.quantity) || 1)));
  }, [store, lines, shipping, discount]); // eslint-disable-line react-hooks/exhaustive-deps
  const taxOnTop = taxLines && taxLines.mode === 'exclusive' ? taxLines.total : 0;
  const total = discountedSubtotal + shipping + taxOnTop;

  const detailsValid = form.name.trim() && isEmail(form.email);
  const addressValid = method === 'collect' || (form.line1.trim() && form.city.trim() && form.postcode.trim());
  const methodValid = method === 'collect' ? pickupEnabled : (shippingEnabled && !!zone && !!selectedRate);
  const canPay = detailsValid && addressValid && methodValid && lines.length > 0 && !paying && !storeLoading;

  const pay = async () => {
    if (!canPay) return;
    // Not signed in (e.g. session expired mid-checkout) — route through the same
    // auth prompt as service bookings / gift cards instead of a dead-end error.
    if (!localStorage.getItem('accessToken')) {
      if (onRequireAuth) { onRequireAuth(); return; }
      setError('Please sign in to complete your purchase.'); return;
    }
    setPaying(true);
    setError('');
    try {
      const payload = {
        salonId,
        items: lines.map((l) => ({ productId: l.id, quantity: l.qty, variantId: l.variantId || undefined })),
        delivery: method === 'ship'
          ? { method: 'delivery', rateId: selectedRate._id, rateName: selectedRate.name, address: { line1: form.line1.trim(), city: form.city.trim(), postcode: form.postcode.trim(), country } }
          : { method: 'pickup' },
        customerNote: form.note.trim(),
        ...(codeOn ? { promo: { code: code.typed } } : {}),
      };
      const res = await productService.createCheckout(payload);
      const data = (res && res.data && (res.data.responseData !== undefined ? res.data.responseData : res.data)) || {};
      const url = data.url || (data.result && data.result.url);
      if (url) { window.location.href = url; return; }
      setError('Could not start checkout. Please try again.');
      setPaying(false);
    } catch (err) {
      const m = err && err.response && err.response.data && (err.response.data.messages || (err.response.data.error && err.response.data.error.responseMessage));
      setError(typeof m === 'string' ? m : 'Checkout failed. Please try again.');
      setPaying(false);
    }
  };

  return (
    <div className="ps-checkout">
      <div className="ps-checkout-body">
        {/* Method */}
        <section className="ps-co-section">
          <h3 className="ps-co-label">How would you like it?</h3>
          <div className="ps-method">
            {shippingEnabled && (
              <button type="button" className={`ps-method-opt ${method === 'ship' ? 'is-active' : ''}`} onClick={() => setMethod('ship')}>
                <span className="ps-method-icon"><TruckIcon /></span>
                <span className="ps-method-text">
                  <span className="ps-method-title">Ship to me</span>
                  <span className="ps-method-sub">{selectedRate ? (shipping > 0 ? `${currency}${shipping.toFixed(2)} delivery` : 'Free delivery') : 'Delivery'}</span>
                </span>
              </button>
            )}
            {pickupEnabled && (
              <button type="button" className={`ps-method-opt ${method === 'collect' ? 'is-active' : ''}`} onClick={() => setMethod('collect')}>
                <span className="ps-method-icon"><StoreIcon /></span>
                <span className="ps-method-text">
                  <span className="ps-method-title">Collect from {salonName || 'the salon'}</span>
                  <span className="ps-method-sub">Free · ready soon</span>
                </span>
              </button>
            )}
          </div>
          {!storeLoading && !shippingEnabled && !pickupEnabled && (
            <p className="ps-co-note">This store isn't accepting orders right now.</p>
          )}
        </section>

        {/* Delivery: address + eligible rates */}
        {method === 'ship' && (
          <section className="ps-co-section">
            <h3 className="ps-co-label">Delivery address</h3>
            <input className="ps-input" placeholder="Address line" value={form.line1} onChange={(e) => upd({ line1: e.target.value })} />
            <div className="ps-input-row">
              <input className="ps-input" placeholder="Town / City" value={form.city} onChange={(e) => upd({ city: e.target.value })} />
              <input className="ps-input" placeholder="Postcode" value={form.postcode} onChange={(e) => upd({ postcode: e.target.value })} />
            </div>
            <select className="ps-input" value={country} onChange={(e) => setCountry(e.target.value)} aria-label="Country">
              {shipCountries.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>

            <h3 className="ps-co-label" style={{ marginTop: 14 }}>Delivery option</h3>
            {(!zone || rates.length === 0) ? (
              <div className="ps-co-undeliverable">
                <p className="ps-co-note">
                  {!zone
                    ? "This order can't be delivered to that country."
                    : "This order can't be delivered. Delivery isn't available for this basket."}
                </p>
                {pickupEnabled ? (
                  <button type="button" className="ps-co-switch-collect" onClick={() => setMethod('collect')}>
                    Collect from {salonName || 'the salon'} instead (free)
                  </button>
                ) : (
                  <p className="ps-co-note">Try removing an item, or contact the salon to order directly.</p>
                )}
              </div>
            ) : (
              rates.map((r) => (
                <label key={r._id} className={`ps-radio-row ${String(rateId) === String(r._id) ? 'is-active' : ''}`}>
                  <input type="radio" name="ship-rate" checked={String(rateId) === String(r._id)} onChange={() => setRateId(String(r._id))} />
                  <span>{r.name}</span>
                  <span className="ps-radio-price">{(Number(r.price) || 0) > 0 ? `${currency}${(Number(r.price) || 0).toFixed(2)}` : 'Free'}</span>
                </label>
              ))
            )}
          </section>
        )}

        {/* Buyer */}
        <section className="ps-co-section">
          <h3 className="ps-co-label">Your details</h3>
          <input className="ps-input" placeholder="Full name" value={form.name} onChange={(e) => upd({ name: e.target.value })} />
          <input className="ps-input" type="email" placeholder="Email" value={form.email} onChange={(e) => upd({ email: e.target.value })} />
          {/* Country dropdown + UK default; value held in E.164 (+44…) so the
              country code always travels with the number. */}
          <PhoneInput key={phoneKey} className="ps-phone" international defaultCountry="GB" countryCallingCodeEditable={false} placeholder="+44 7700 900000 (optional)" value={form.phone} onChange={(value) => upd({ phone: dropTrunkZero(value) || '' })} onBlur={() => setPhoneKey((k) => k + 1)} />
          <textarea className="ps-input ps-textarea" placeholder={method === 'collect' ? 'Note for the salon (optional)' : 'Delivery note (optional)'} rows={2} value={form.note} onChange={(e) => upd({ note: e.target.value })} />
        </section>

        {/* Summary */}
        <section className="ps-co-section">
          <h3 className="ps-co-label">Order summary</h3>
          <div className="ps-co-summary">
            {lines.map((l) => (
              <div className="ps-co-row" key={l.id}><span>{l.qty}× {l.name}</span><span>{currency}{l.lineTotal.toFixed(2)}</span></div>
            ))}
            <div className="ps-co-row"><span>Subtotal</span><span>{currency}{subtotal.toFixed(2)}</span></div>
            {codeOn && (
              <div className="ps-co-row ps-co-row--discount"><span>Code {code.result.code ? code.result.code.code : code.typed}</span><span>−{currency}{discount.toFixed(2)}</span></div>
            )}
            <div className="ps-co-row"><span>{method === 'collect' ? 'Collection' : 'Shipping'}</span><span>{shipping > 0 ? `${currency}${shipping.toFixed(2)}` : 'Free'}</span></div>
            {taxLines && taxLines.rows.map((r) => (
              <div className="ps-co-row ps-co-row--tax" key={r.label}>
                <span>{taxLines.mode === 'exclusive' ? r.label : `Includes ${r.label}`}</span>
                <span>{taxLines.mode === 'exclusive' ? '+' : ''}{currency}{r.amount.toFixed(2)}</span>
              </div>
            ))}
            <div className="ps-co-row ps-co-row--total"><span>Total</span><strong>{currency}{total.toFixed(2)}</strong></div>
          </div>
          <CodeBox code={code} ctx={codeCtx} currency={currency} noun="order" />
        </section>

        {error && <p className="ps-co-error" role="alert">{error}</p>}
      </div>

      <div className="ps-checkout-bar">
        <button type="button" className="ps-btn ps-btn--ghost" onClick={onBack}>Back</button>
        <button type="button" className={`ps-btn ps-btn--primary ps-btn--lg ${paying ? 'is-loading' : ''}`} disabled={!canPay} onClick={pay}>
          {paying ? 'Processing…' : `Pay ${currency}${total.toFixed(2)}`}
        </button>
      </div>
    </div>
  );
};

export default ProductCheckout;
